import React, { useState, useEffect } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { PopoverContent, PopoverProvider, PopoverTarget } from '@atlaskit/spotlight';

import { token } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import Image from '@atlaskit/image';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useRouterActions } from '@atlassian/react-resource-router';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useSeenStatus } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/controllers/seen-status/index.tsx';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import stakeholdersImage from '../../assets/stakeholders.svg';
import { messages } from './messages.tsx';

const styles = cssMap({
	spotlightTarget: {
		border: `${token('border.width.selected')} solid ${token('color.border.discovery')}`,
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		transform: `translate(${token('space.negative.050')})`,
	},
	spotlightContainer: {
		position: 'relative',
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		insetBlockStart: '-26px',
	},
	spotlightContent: {
		position: 'absolute',
		width: '349px',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		backgroundColor: token('color.background.discovery.bold'),
		borderRadius: token('radius.medium'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	spotlightContentHeader: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	spotlightContentBody: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	spotlightContentFooter: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
});

interface ChangeboardingProps {
	children: React.ReactNode;
	href: string;
	isExpanded: boolean;
}

export const CommsChangeboarding = ({ children, href, isExpanded }: ChangeboardingProps) => {
	const { push } = useRouterActions();

	const [isActive, setIsActive] = useState(false);

	const [{ isSeen }, { markAsSeen }] = useSeenStatus({
		userPropertyKey: 'communications.stakeholder-management.changeboarding.commsTab.seenStatus',
		isSeenValue: true,
		packageName: 'stakeholder-management',
		teamName: 'iris',
	});

	useEffect(() => {
		if (!isSeen && isExpanded) {
			setIsActive(true);
		}
	}, [isSeen, isExpanded]);

	return (
		<PopoverProvider>
			<PopoverTarget>
				{isActive ? <Box xcss={styles.spotlightTarget}>{children}</Box> : children}
			</PopoverTarget>
			<PopoverContent
				placement="right-end"
				isVisible={isActive}
				dismiss={() => {
					/* empty */
				}}
			>
				<Box xcss={styles.spotlightContainer}>
					<Flex xcss={styles.spotlightContent} direction="column" gap="space.200">
						<Flex xcss={styles.spotlightContentHeader} justifyContent="center" alignItems="center">
							<Text size="medium" weight="bold" color="color.text.inverse">
								<FormattedMessage {...messages.commsTabTourHeading} />
							</Text>
							<IconButton
								appearance="subtle"
								onClick={(_, event) => {
									fireUIAnalytics(event, 'changeboardingStep1Dismissed');
									setIsActive(false);
									markAsSeen();
								}}
								icon={(iconProps) => (
									<CrossIcon {...iconProps} color={token('color.icon.subtle')} />
								)}
								label={<FormattedMessage {...messages.commsTabTourDismissIconLabel} />}
							/>
						</Flex>
						<Image src={stakeholdersImage} alt="" width={349} height={140} />
						<Box xcss={styles.spotlightContentBody}>
							<Text size="medium" color="color.text.inverse">
								<FormattedMessage {...messages.commsTabTourBody} />
							</Text>
						</Box>
						<Flex justifyContent="end" xcss={styles.spotlightContentFooter}>
							<Button
								appearance="discovery"
								spacing="compact"
								onClick={(_, event) => {
									fireUIAnalytics(event, 'changeboardingStep1ExploreNowClicked');
									setIsActive(false);
									markAsSeen();
									setTimeout(() => {
										push(href);
									}, 0);
								}}
							>
								<FormattedMessage {...messages.commsTabTourAction} />
							</Button>
						</Flex>
					</Flex>
				</Box>
			</PopoverContent>
		</PopoverProvider>
	);
};
